import React from "react";
import { Box, Tooltip, Typography } from "@material-ui/core";
import { CustomChip } from "components";
import { SectionDefinitionValue } from "components/SchemaProvider";

interface ReadOnlyFieldProps {
    dateTime?: boolean;
    date?: boolean;
    tooltip?: string;
    caption?: string;
    label?: SectionDefinitionValue;
    colour?: string;
    paddingY?: number;
    paddingX?: number;
    bold?: boolean;
    reversed?: boolean;
}

interface LabelProps {
    dateTime?: boolean;
    date?: boolean;
    label: SectionDefinitionValue;
    colour?: string;
    bold?: boolean;
}

const Text = ({ label, bold = false }: { label: string; bold?: boolean }) => {
    return (
        <Typography component="div">
            <Box
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                overflow="hidden"
                fontWeight={bold ? "fontWeightBold" : "normal"}>
                {label}
            </Box>
        </Typography>
    );
};

const Label = ({ colour = "", label, dateTime = false, date = false, bold = false }: LabelProps) => {
    if (Array.isArray(label)) {
        // example of properties for contact field
        // {"id": 1, "contactId": 37151, "value": "rebecca@hali.com.au"}
        return <Text label={label.map((props) => props["value"]).join(", ")} bold={bold} />;
    }

    if (date) {
        return <Text label={label ? new Date(label).toLocaleDateString() : "-"} bold={bold} />;
    }

    if (dateTime) {
        return <Text label={label ? new Date(label).toLocaleString() : "-"} bold={bold} />;
    }

    return colour ? <CustomChip bgColor={colour} label={label} /> : <Text label={label || "-"} bold={bold} />;
};

export const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
    reversed = false,
    dateTime = false,
    date = false,
    bold = false,
    paddingX = 1,
    paddingY = 1,
    caption = "",
    label = "-",
    tooltip,
    colour,
    children,
}) => {
    return (
        <Box
            display="flex"
            paddingY={paddingY}
            paddingX={paddingX}
            flexDirection={reversed ? "column-reverse" : "column"}>
            {caption && (
                <Typography variant="caption" color="textSecondary">
                    {caption}
                </Typography>
            )}
            {children ? (
                <Typography component="div" variant="body1">
                    {children}
                </Typography>
            ) : (
                <Typography component="div" variant="body1" style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
                    {tooltip ? (
                        <Tooltip title={tooltip} arrow placement="bottom-start">
                            <>
                                <Label colour={colour} label={label} bold={bold} />
                            </>
                        </Tooltip>
                    ) : (
                        <Label colour={colour} label={label} dateTime={dateTime} date={date} bold={bold} />
                    )}
                </Typography>
            )}
        </Box>
    );
};
